<template>
  <b-modal
    :id="modalId"
    title="Cắt ảnh"
    size="lg"
    hide-footer
    @show="onModalShow"
    @hide="onModalHide"
  >
    <div class="image-cropper-modal">
      <!-- Thông báo lỗi nếu không có ảnh -->
      <div v-if="!hasValidImage" class="alert alert-warning">
        <i class="fa-solid fa-exclamation-triangle mr-2"></i>
        Không có ảnh để cắt. Vui lòng chọn ảnh trước.
      </div>

      <!-- Khu vực crop ảnh -->
      <div v-if="hasValidImage" class="crop-container">
        <div class="crop-area">
          <canvas
            ref="canvas"
            @mousedown="startDrag"
            @mousemove="drag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
            style="border: 1px solid #ccc; cursor: crosshair; max-width: 100%;"
          ></canvas>
        </div>

        <div class="crop-controls mt-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
<!--              <label class="mr-3 mb-0">-->
<!--                <input type="checkbox" v-model="maintainAspectRatio" class="mr-1">-->
<!--                Giữ tỷ lệ khung hình-->
<!--              </label>-->
              <button @click="resetCrop" class="btn btn-outline-secondary btn-sm">
                <i class="fa-solid fa-rotate-right mr-1"></i>
                Reset
              </button>
            </div>
            <div>
              <span class="text-muted small mr-3">
                Kích thước: {{ cropRect.width.toFixed(0) }} x {{ cropRect.height.toFixed(0) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview ảnh đã cắt -->
      <div v-if="previewImage" class="preview-container mt-3">
        <hr>
        <h6>Preview ảnh đã cắt:</h6>
        <div class="text-center">
          <img :src="previewImage" alt="Preview" class="preview-img" />
        </div>
      </div>

      <!-- Buttons -->
      <div class="modal-actions mt-2">
        <div class="d-flex justify-content-end">
          <button @click="closeModal" class="btn btn-secondary mr-2">
            <i class="fa-solid fa-times mr-1"></i>
            Hủy
          </button>
          <button
            v-if="hasValidImage"
            @click="previewCrop"
            class="btn btn-info mr-2"
            :disabled="!hasValidImage"
          >
            <i class="fa-solid fa-eye mr-1"></i>
            Preview
          </button>
          <button
            @click="cropAndSave"
            class="btn btn-primary"
            :disabled="!hasValidImage"
          >
            Cắt và Lưu
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ImageCropper',
  props: {
    modalId: {
      type: String,
      default: 'image-cropper-modal'
    },
    imageData: {
      type: String,
      default: ''
    },
    width: {
      type: Number,
      default: 600
    },
    height: {
      type: Number,
      default: 400
    }
  },
  data() {
    return {
      originalImage: null,
      previewImage: '',
      isDragging: false,
      dragMode: 'none', // 'move', 'resize', 'create'
      activeHandle: null,
      startX: 0,
      startY: 0,
      cropRect: {
        x: 0,
        y: 0,
        width: 200,
        height: 200
      },
      maintainAspectRatio: false,
      aspectRatio: 1,
      canvas: null,
      ctx: null,
      canvasScale: 1
    }
  },
  computed: {
    hasValidImage() {
      return this.imageData && this.imageData.length > 0;
    }
  },
  watch: {
    imageData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.previewImage = '';
          this.$nextTick(() => {
            this.loadImageToCanvas();
          });
        }
      }
    },
    maintainAspectRatio(newVal) {
      if (newVal) {
        this.aspectRatio = this.cropRect.width / this.cropRect.height;
      }
    }
  },
  methods: {
    onModalShow() {
      if (this.hasValidImage) {
        this.$nextTick(() => {
          this.loadImageToCanvas();
        });
      }
    },

    onModalHide() {
      this.previewImage = '';
      this.isDragging = false;
    },

    loadImageToCanvas() {
      if (!this.hasValidImage) return;

      this.canvas = this.$refs.canvas;
      if (!this.canvas) return;

      this.ctx = this.canvas.getContext('2d');

      const img = new Image();
      img.onload = () => {
        this.originalImage = img;

        // Tính toán kích thước canvas phù hợp
        const maxWidth = this.width;
        const maxHeight = this.height;
        let canvasWidth = img.width;
        let canvasHeight = img.height;

        // Scale ảnh để fit trong modal
        if (img.width > maxWidth || img.height > maxHeight) {
          const widthRatio = maxWidth / img.width;
          const heightRatio = maxHeight / img.height;
          const ratio = Math.min(widthRatio, heightRatio);

          canvasWidth = img.width * ratio;
          canvasHeight = img.height * ratio;
        }

        this.canvas.width = canvasWidth;
        this.canvas.height = canvasHeight;
        this.canvasScale = canvasWidth / img.width;

        // Khởi tạo crop area ở giữa (20% margin)
        const margin = 0.1;
        this.cropRect = {
          x: canvasWidth * margin,
          y: canvasHeight * margin,
          width: canvasWidth * (1 - 2 * margin),
          height: canvasHeight * (1 - 2 * margin)
        };

        this.aspectRatio = this.cropRect.width / this.cropRect.height;
        this.drawCanvas();
      };
      img.src = this.imageData;
    },

    drawCanvas() {
      if (!this.ctx || !this.originalImage) return;

      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // Vẽ ảnh gốc
      this.ctx.drawImage(this.originalImage, 0, 0, this.canvas.width, this.canvas.height);

      // Vẽ overlay tối
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

      // Clear crop area để hiển thị ảnh gốc
      this.ctx.clearRect(this.cropRect.x, this.cropRect.y, this.cropRect.width, this.cropRect.height);

      // Vẽ lại ảnh trong crop area
      this.ctx.drawImage(
        this.originalImage,
        this.cropRect.x / this.canvasScale,
        this.cropRect.y / this.canvasScale,
        this.cropRect.width / this.canvasScale,
        this.cropRect.height / this.canvasScale,
        this.cropRect.x,
        this.cropRect.y,
        this.cropRect.width,
        this.cropRect.height
      );

      // Vẽ border crop area
      this.ctx.strokeStyle = '#007bff';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(this.cropRect.x, this.cropRect.y, this.cropRect.width, this.cropRect.height);

      // Vẽ handles
      this.drawHandles();
    },

    drawHandles() {
      const handleSize = 10;
      this.ctx.fillStyle = '#007bff';
      this.ctx.strokeStyle = '#ffffff';
      this.ctx.lineWidth = 2;

      const handles = [
        {x: this.cropRect.x, y: this.cropRect.y}, // top-left
        {x: this.cropRect.x + this.cropRect.width, y: this.cropRect.y}, // top-right
        {x: this.cropRect.x, y: this.cropRect.y + this.cropRect.height}, // bottom-left
        {x: this.cropRect.x + this.cropRect.width, y: this.cropRect.y + this.cropRect.height} // bottom-right
      ];

      handles.forEach(handle => {
        this.ctx.fillRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
        this.ctx.strokeRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
      });
    },

    getMousePos(event) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) * (this.canvas.width / rect.width),
        y: (event.clientY - rect.top) * (this.canvas.height / rect.height)
      };
    },

    startDrag(event) {
      const pos = this.getMousePos(event);
      this.isDragging = true;
      this.startX = pos.x;
      this.startY = pos.y;

      // Xác định drag mode
      const handleSize = 10;
      const handle = this.getActiveHandle(pos.x, pos.y, handleSize);

      if (handle) {
        this.dragMode = 'resize';
        this.activeHandle = handle;
      } else if (this.isInsideCropArea(pos.x, pos.y)) {
        this.dragMode = 'move';
      } else {
        this.dragMode = 'create';
      }
    },

    drag(event) {
      if (!this.isDragging) return;

      const pos = this.getMousePos(event);

      switch(this.dragMode) {
        case 'move':
          this.moveCropArea(pos.x - this.startX, pos.y - this.startY);
          this.startX = pos.x;
          this.startY = pos.y;
          break;
        case 'resize':
          this.resizeCropArea(pos.x, pos.y);
          break;
        case 'create':
          this.createCropArea(pos.x, pos.y);
          break;
      }

      this.constrainCropArea();
      this.drawCanvas();
    },

    endDrag() {
      this.isDragging = false;
      this.dragMode = 'none';
      this.activeHandle = null;
    },

    getActiveHandle(x, y, handleSize) {
      const handles = [
        {id: 'tl', x: this.cropRect.x, y: this.cropRect.y},
        {id: 'tr', x: this.cropRect.x + this.cropRect.width, y: this.cropRect.y},
        {id: 'bl', x: this.cropRect.x, y: this.cropRect.y + this.cropRect.height},
        {id: 'br', x: this.cropRect.x + this.cropRect.width, y: this.cropRect.y + this.cropRect.height}
      ];

      return handles.find(handle =>
        Math.abs(x - handle.x) <= handleSize && Math.abs(y - handle.y) <= handleSize
      );
    },

    isInsideCropArea(x, y) {
      return x >= this.cropRect.x && x <= this.cropRect.x + this.cropRect.width &&
             y >= this.cropRect.y && y <= this.cropRect.y + this.cropRect.height;
    },

    moveCropArea(deltaX, deltaY) {
      this.cropRect.x += deltaX;
      this.cropRect.y += deltaY;
    },

    createCropArea(x, y) {
      this.cropRect.x = Math.min(this.startX, x);
      this.cropRect.y = Math.min(this.startY, y);
      this.cropRect.width = Math.abs(x - this.startX);
      this.cropRect.height = Math.abs(y - this.startY);

      if (this.maintainAspectRatio && this.cropRect.width > 0) {
        this.cropRect.height = this.cropRect.width / this.aspectRatio;
      }
    },

    resizeCropArea(x, y) {
      if (!this.activeHandle) return;

      const oldRect = {...this.cropRect};

      switch(this.activeHandle.id) {
        case 'tl':
          this.cropRect.width += this.cropRect.x - x;
          this.cropRect.height += this.cropRect.y - y;
          this.cropRect.x = x;
          this.cropRect.y = y;
          break;
        case 'tr':
          this.cropRect.width = x - this.cropRect.x;
          this.cropRect.height += this.cropRect.y - y;
          this.cropRect.y = y;
          break;
        case 'bl':
          this.cropRect.width += this.cropRect.x - x;
          this.cropRect.height = y - this.cropRect.y;
          this.cropRect.x = x;
          break;
        case 'br':
          this.cropRect.width = x - this.cropRect.x;
          this.cropRect.height = y - this.cropRect.y;
          break;
      }

      if (this.maintainAspectRatio) {
        if (Math.abs(this.cropRect.width - oldRect.width) > Math.abs(this.cropRect.height - oldRect.height)) {
          this.cropRect.height = this.cropRect.width / this.aspectRatio;
        } else {
          this.cropRect.width = this.cropRect.height * this.aspectRatio;
        }
      }
    },

    constrainCropArea() {
      // Đảm bảo crop area nằm trong canvas
      if (this.cropRect.x < 0) {
        this.cropRect.width += this.cropRect.x;
        this.cropRect.x = 0;
      }
      if (this.cropRect.y < 0) {
        this.cropRect.height += this.cropRect.y;
        this.cropRect.y = 0;
      }
      if (this.cropRect.x + this.cropRect.width > this.canvas.width) {
        this.cropRect.width = this.canvas.width - this.cropRect.x;
      }
      if (this.cropRect.y + this.cropRect.height > this.canvas.height) {
        this.cropRect.height = this.canvas.height - this.cropRect.y;
      }

      // Đảm bảo kích thước tối thiểu
      this.cropRect.width = Math.max(20, this.cropRect.width);
      this.cropRect.height = Math.max(20, this.cropRect.height);
    },

    resetCrop() {
      const margin = 0.1;
      this.cropRect = {
        x: this.canvas.width * margin,
        y: this.canvas.height * margin,
        width: this.canvas.width * (1 - 2 * margin),
        height: this.canvas.height * (1 - 2 * margin)
      };
      this.drawCanvas();
    },

    previewCrop() {
      this.previewImage = this.generateCroppedImage();
    },

    generateCroppedImage() {
      if (!this.originalImage) return '';

      // Tạo canvas mới cho ảnh đã crop
      const cropCanvas = document.createElement('canvas');
      const cropCtx = cropCanvas.getContext('2d');

      // Tính toán kích thước thực tế trên ảnh gốc
      const scaleX = this.originalImage.width / this.canvas.width;
      const scaleY = this.originalImage.height / this.canvas.height;

      const sourceX = this.cropRect.x * scaleX;
      const sourceY = this.cropRect.y * scaleY;
      const sourceWidth = this.cropRect.width * scaleX;
      const sourceHeight = this.cropRect.height * scaleY;

      cropCanvas.width = sourceWidth;
      cropCanvas.height = sourceHeight;

      // Vẽ phần đã crop từ ảnh gốc
      cropCtx.drawImage(
        this.originalImage,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        sourceWidth,
        sourceHeight
      );

      return cropCanvas.toDataURL('image/jpeg', 0.9);
    },

    cropAndSave() {
      const croppedImage = this.generateCroppedImage();
      this.$emit('cropped', croppedImage);
      this.closeModal();
    },

    closeModal() {
      this.$bvModal.hide(this.modalId);
    }
  }
}
</script>

<style scoped>
.image-cropper-modal {
  min-height: 200px;
}

.crop-container {
  text-align: center;
}

.crop-area canvas {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.preview-container {
  max-height: 200px;
  overflow-y: auto;
}

.preview-img {
  max-width: 100%;
  max-height: 150px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.crop-controls {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
}

.modal-actions {
  background-color: #f8f9fa;
  margin: -15px -15px -5px -15px;
  padding: 10px;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .crop-controls .d-flex {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
